import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Check, Loader2, Building2, Network, Search } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { AccountApi } from '@/services/account.api';
import { OrgAuthApi } from '@/services/org.api';
import { toast } from '@/stores/toast.store';

/**
 * In-app school switcher. Works for two "origins":
 *  - multi-school account sessions (accountToken + >1 granted schools)
 *  - organization-admin sessions (orgToken) — also offers "Back to organization"
 * Switching re-selects the school (new token) and clears cached data.
 */
export function SchoolSwitcher() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const schoolSlug = useAuthStore((s) => s.schoolSlug);
  const accessToken = useAuthStore((s) => s.accessToken);
  const enterSchoolSession = useAuthStore((s) => s.enterSchoolSession);
  const setOrgSession = useAuthStore((s) => s.setOrgSession);
  const accountToken = useAuthStore((s) => s.accountToken);
  const accountSchools = useAuthStore((s) => s.accountSchools);
  const orgToken = useAuthStore((s) => s.orgToken);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [exiting, setExiting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const isOrg = !!orgToken;
  const isAccount = !isOrg && !!accountToken && (accountSchools?.length ?? 0) >= 2;

  // Live fetch (not a login-time snapshot) — org school lists can be 50+ and
  // change over time, so the switcher always sees the current, real list.
  // Fetches on mount (not gated on `open`) — otherwise the button shows the
  // generic "Select school" fallback forever until the user opens the
  // dropdown once, even while genuinely inside a specific school.
  const { data: orgSchoolsPage, isLoading: orgSchoolsLoading } = useQuery({
    queryKey: ['org-schools-switcher', orgToken],
    queryFn: () => OrgAuthApi.myOrgSchools({ page: 1, limit: 100 }, orgToken!),
    enabled: isOrg,
  });

  if (!isOrg && !isAccount) return null;

  const schools = isOrg
    ? (orgSchoolsPage?.items ?? []).map((s) => ({
        schoolId: s.id,
        code: s.code ?? '',
        slug: s.slug,
        name: s.name,
        role: 'admin',
      }))
    : (accountSchools ?? []);
  const current = schools.find((s) => s.slug === schoolSlug);
  const term = search.trim().toLowerCase();
  const filteredSchools = term
    ? schools.filter(
        (s) => s.name.toLowerCase().includes(term) || s.code.toLowerCase().includes(term),
      )
    : schools;

  const switchTo = async (schoolId: string, slug: string) => {
    if (slug === schoolSlug) {
      setOpen(false);
      return;
    }
    setBusy(schoolId);
    try {
      const session = isOrg
        ? await OrgAuthApi.switchSchool(schoolId, orgToken!)
        : await AccountApi.selectSchool(schoolId, accountToken!);
      enterSchoolSession({
        user: {
          id: session.user.id,
          name: session.user.name,
          email: session.user.email,
          role: session.user.role,
          schoolId: session.user.schoolId,
          schoolSlug: session.user.schoolSlug,
          scope: isOrg ? 'school' : 'tenant',
          // Only meaningful (and only reliably populated by the backend) for
          // the org-admin-acting-as-school path — the account/multi-school
          // path is always a real `scope: 'tenant'` session, never subject
          // to the dashboard gating this field exists for.
          isSchemaProvisioned: isOrg ? session.school.isSchemaProvisioned : undefined,
        },
        accessToken: session.tokens.accessToken,
        refreshToken: session.tokens.refreshToken,
        schoolSlug: session.school.slug,
      });
      qc.clear();
      setOpen(false);
      navigate('/dashboard');
      toast.success(`Switched to ${session.school.slug}`);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error?.message ?? e?.message ?? 'Could not switch',
      );
    } finally {
      setBusy(null);
    }
  };

  const goToOrg = async () => {
    if (!accessToken) return;
    setExiting(true);
    try {
      const res = await OrgAuthApi.exitSchool();
      setOrgSession({
        user: {
          id: res.admin.id,
          name: res.admin.name,
          email: res.admin.email ?? '',
          role: res.admin.role,
          organizationId: res.admin.organizationId,
          scope: 'organization',
        },
        accessToken: res.tokens.accessToken,
        refreshToken: res.tokens.refreshToken,
      });
      qc.clear();
      setOpen(false);
      navigate('/org');
    } catch (e: any) {
      // Diagnostic trail for the recurring "back to organization logs me
      // out" report — every token/refresh path this call can hit has been
      // verified correct against the live backend (including an actually-
      // expired access token + valid refresh token end-to-end), so this
      // logs the exact failure (status/code/message, and whether the
      // refresh-retry itself was what failed) instead of leaving only a
      // generic toast behind the next time it happens.
      // eslint-disable-next-line no-console
      console.error('[goToOrg] exit-school failed', {
        status: e?.response?.status,
        code: e?.response?.data?.error?.code,
        message: e?.response?.data?.error?.message ?? e?.message,
        isAxiosError: e?.isAxiosError,
        url: e?.config?.url,
      });
      toast.error(
        e?.response?.data?.error?.message ?? e?.message ?? 'Could not exit school',
      );
    } finally {
      setExiting(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 hover:border-brand-400"
        title="Switch school"
      >
        <Building2 className="h-4 w-4 text-slate-400" />
        <span className="max-w-[10rem] truncate font-medium">
          {current?.name ?? schoolSlug ?? 'Select school'}
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-1 w-64 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
            {isOrg && (
              <>
                <button
                  onClick={goToOrg}
                  disabled={exiting}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                >
                  {exiting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Network className="h-4 w-4" />
                  )}
                  Back to organization
                </button>
                <div className="my-1 border-t border-slate-100" />
              </>
            )}
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Switch school
            </div>
            {schools.length > 0 && (
              <div className="px-2 pb-1.5">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search schools…"
                    className="w-full rounded-md border border-slate-200 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-brand-400"
                  />
                </div>
              </div>
            )}
            <div className="max-h-72 overflow-y-auto">
              {isOrg && orgSchoolsLoading ? (
                <div className="px-3 py-2 text-xs text-slate-400">Loading…</div>
              ) : (
                filteredSchools.map((s) => {
                  const active = s.slug === schoolSlug;
                  return (
                    <button
                      key={s.schoolId}
                      onClick={() => switchTo(s.schoolId, s.slug)}
                      disabled={!!busy}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-60"
                    >
                      <span className="leading-tight">
                        <span className="block font-medium text-slate-900">
                          {s.name}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {s.code} · {s.role}
                        </span>
                      </span>
                      {busy === s.schoolId ? (
                        <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
                      ) : active ? (
                        <Check className="h-4 w-4 text-brand-600" />
                      ) : null}
                    </button>
                  );
                })
              )}
              {!orgSchoolsLoading && schools.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">
                  No schools yet.
                </div>
              )}
              {!orgSchoolsLoading && schools.length > 0 && filteredSchools.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">
                  No schools match "{search}".
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
